import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
// Custom components
import { findCurrentAcademicYear } from "components/SelectAcademicYear";
// API calls
import { getAcademicYear } from "utils/requests/schoolPlan/academicYear";

const ACADEMIC_YEAR_STORAGE_KEY = "askuala_academic_year";

const AcademicYearContext = createContext(null);

/**
 * AcademicYearProvider holds the academic year selected from the settings drawer so every
 * part of the project can read it, mirroring how the region (locale) selection works.
 * The selection persists in localStorage and defaults to the ongoing academic year.
 * The year is resolved again whenever a session starts or the active school changes.
 * @param {JSX.Element} children - The components that can consume the academic year
 * @param {boolean} isAuthenticated - Whether a session is active, nothing is fetched without one
 * @param {string} schoolId - The active school, a change triggers a fresh resolution
 * @returns {JSX.Element} The provider wrapping its children
 * @example
 * <AcademicYearProvider isAuthenticated={isAuth} schoolId={profile.school_id}>
 *   <App />
 * </AcademicYearProvider>
 */
export function AcademicYearProvider({ children, isAuthenticated, schoolId }) {
  const [academicYearId, setAcademicYearIdState] = useState(null);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    // Drop the previous session's year so pages wait for the new one
    setAcademicYearIdState(null);
    setAcademicYears([]);
    setLoading(true);

    if (!isAuthenticated) return undefined;

    const resolveAcademicYear = async () => {
      try {
        const response = await getAcademicYear();
        if (!isMounted) return;
        if (response.success) {
          const fetchedAcademicYears = response.data?.academic_years ?? [];
          setAcademicYears(
            fetchedAcademicYears.map((academicYear) => ({
              id: academicYear.academic_year_id,
              label: academicYear.label,
            })),
          );

          const savedAcademicYearId = localStorage.getItem(ACADEMIC_YEAR_STORAGE_KEY);
          const initialAcademicYear =
            fetchedAcademicYears.find(
              (academicYear) => academicYear.academic_year_id === savedAcademicYearId,
            ) ?? findCurrentAcademicYear(fetchedAcademicYears);

          if (initialAcademicYear) {
            const initialId = initialAcademicYear.academic_year_id;
            localStorage.setItem(ACADEMIC_YEAR_STORAGE_KEY, initialId);
            setAcademicYearIdState(initialId);
          }
        }
      } catch (error) {
        console.error("Error resolving the global academic year:", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    resolveAcademicYear();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, schoolId]);

  const setAcademicYear = useCallback((id) => {
    if (!id) return;
    localStorage.setItem(ACADEMIC_YEAR_STORAGE_KEY, id);
    setAcademicYearIdState(id);
  }, []);

  const value = { academicYearId, setAcademicYear, academicYears, loading };

  return <AcademicYearContext.Provider value={value}>{children}</AcademicYearContext.Provider>;
}

/**
 * useAcademicYear gives access to the globally selected academic year
 * @returns {{academicYearId: string|null, setAcademicYear: Function, academicYears: Array,
 * loading: boolean}} The selected academic year id, its setter, the academic year options,
 * and the resolution loading state
 * @example
 * const { academicYearId, setAcademicYear, academicYears, loading } = useAcademicYear();
 */
export function useAcademicYear() {
  const ctx = useContext(AcademicYearContext);
  if (!ctx) throw new Error("useAcademicYear must be used inside <AcademicYearProvider>");
  return ctx;
}

AcademicYearProvider.propTypes = {
  children: PropTypes.node.isRequired,
  isAuthenticated: PropTypes.bool,
  schoolId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
